import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown, Button, Badge } from 'react-bootstrap';
import Icon from '@mdi/react';
import {
  mdiChevronRight,
  mdiChevronDown,
  mdiFilterVariant,
  mdiMagnify,
  mdiClose,
  mdiUnfoldMoreHorizontal,
  mdiUnfoldLessHorizontal,
} from '@mdi/js';

import './styles.css';

const JKR = 'JKR';

const deptName = (dept) => dept?.short_form || dept?.value || '';

// A small controlled checkbox that can render the indeterminate (mixed) state.
const TriCheckbox = ({ id, checked, indeterminate, onChange, label, className = '' }) => (
  <div className={`form-check d-flex align-items-center mb-0 ${className}`}>
    <input
      ref={(el) => {
        if (el) el.indeterminate = !checked && !!indeterminate;
      }}
      className="form-check-input flex-shrink-0"
      type="checkbox"
      id={id}
      checked={checked}
      onChange={onChange}
    />
    <label
      className="form-check-label ms-2 text-truncate"
      htmlFor={id}
      style={{ cursor: 'pointer' }}
    >
      {label}
    </label>
  </div>
);

// Custom toggle: a labelled button that also summarises the APPLIED selection
// ("JKR, JPS +3") so the user can see what is filtering without opening it.
const FilterToggle = React.forwardRef(({ count, summary, onClick }, ref) => {
  const active = count > 0;
  const button = (
    <Button
      ref={ref}
      variant={active ? 'primary' : 'outline-secondary'}
      onClick={onClick}
      className="d-flex align-items-center"
      style={{ height: 34, maxWidth: 420 }}
    >
      <Icon path={mdiFilterVariant} size={0.8} className="flex-shrink-0" />
      <span className="ms-2 text-truncate">{active ? summary : 'Kementerian / Jabatan'}</span>
      {active && (
        <Badge bg="light" text="primary" pill className="ms-2 flex-shrink-0">
          {count}
        </Badge>
      )}
      <Icon path={mdiChevronDown} size={0.7} className="ms-1 flex-shrink-0" />
    </Button>
  );
  return button;
});

const MinistryFilter = ({ ministries = [], onChange }) => {
  // Selection is keyed by unique ids (short_form is not unique across ministries).
  // selDepts:    department ids fully selected (incl. whole JKR by its dept id)
  // selBranches: JKR branch ids selected when JKR is only partially selected
  // The *applied* selection (what the table is filtered by) is kept apart from
  // the draft edited inside the menu; closing without Tapis discards the draft.
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState('');
  const [selDepts, setSelDepts] = useState(() => new Set());
  const [selBranches, setSelBranches] = useState(() => new Set());
  const [applied, setApplied] = useState(() => ({ depts: new Set(), branches: new Set() }));
  const [expandedMinistries, setExpandedMinistries] = useState(() => new Set());
  const [expandedDepts, setExpandedDepts] = useState(() => new Set());

  const toggleInSet = (set, key) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  };

  const isJkrDept = (dept) => dept.short_form === JKR;
  const isDeptFullyChecked = (dept) => selDepts.has(dept.id);
  const isJkrPartiallyChecked = (dept) =>
    isJkrDept(dept) &&
    !selDepts.has(dept.id) &&
    (dept.branches ?? []).some((b) => selBranches.has(b.id));

  // Per-ministry counts: how many departments are (fully or partly) selected.
  const ministryState = (ministry) => {
    const depts = ministry.departments ?? [];
    const checkedCount = depts.filter(isDeptFullyChecked).length;
    const anyCount = depts.filter((d) => isDeptFullyChecked(d) || isJkrPartiallyChecked(d)).length;
    return {
      checked: depts.length > 0 && checkedCount === depts.length,
      indeterminate: anyCount > 0 && checkedCount !== depts.length,
      selected: anyCount,
      total: depts.length,
    };
  };

  const sortedMinistries = useMemo(
    () => [...ministries].sort((a, b) => Number(a.code) - Number(b.code)),
    [ministries]
  );

  // Lookups for the selected-item chips and the toggle summary.
  const { deptById, branchById } = useMemo(() => {
    const d = new Map();
    const b = new Map();
    sortedMinistries.forEach((m) =>
      (m.departments ?? []).forEach((dept) => {
        d.set(dept.id, dept);
        (dept.branches ?? []).forEach((branch) => b.set(branch.id, { ...branch, dept }));
      })
    );
    return { deptById: d, branchById: b };
  }, [sortedMinistries]);

  // Search filter — keep a ministry when its name matches, otherwise keep only
  // its matching departments/branches. Matching auto-expands the tree.
  const term = search.trim().toLowerCase();
  const matches = (s) => (s ?? '').toString().toLowerCase().includes(term);
  const visibleMinistries = useMemo(() => {
    if (!term) return sortedMinistries;
    return sortedMinistries
      .map((m) => {
        if (matches(m.name)) return m;
        const depts = (m.departments ?? []).filter(
          (d) => matches(d.value) || matches(d.short_form) || (d.branches ?? []).some((b) => matches(b.label))
        );
        return depts.length ? { ...m, departments: depts } : null;
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedMinistries, term]);

  const handleToggleDept = (dept) => {
    if (isJkrDept(dept)) {
      // Whole-JKR checkbox: clear any branch sub-selection either way.
      setSelBranches((prev) => {
        const next = new Set(prev);
        (dept.branches ?? []).forEach((b) => next.delete(b.id));
        return next;
      });
    }
    setSelDepts((prev) => toggleInSet(prev, dept.id));
  };

  const handleToggleBranch = (dept, branch) => {
    const branchIds = (dept.branches ?? []).map((b) => b.id);
    const nextBranches = toggleInSet(selBranches, branch.id);
    const nextDepts = new Set(selDepts);
    nextDepts.delete(dept.id); // partial selection — JKR is no longer "whole"

    // If every JKR branch ends up selected, collapse to whole JKR.
    if (branchIds.length > 0 && branchIds.every((id) => nextBranches.has(id))) {
      branchIds.forEach((id) => nextBranches.delete(id));
      nextDepts.add(dept.id);
    }
    setSelBranches(nextBranches);
    setSelDepts(nextDepts);
  };

  const handleToggleMinistry = (ministry) => {
    const depts = ministry.departments ?? [];
    const { checked } = ministryState(ministry);
    const turnOn = !checked;

    setSelDepts((prev) => {
      const next = new Set(prev);
      depts.forEach((d) => (turnOn ? next.add(d.id) : next.delete(d.id)));
      return next;
    });
    setSelBranches((prev) => {
      const next = new Set(prev);
      depts.filter(isJkrDept).forEach((d) => (d.branches ?? []).forEach((b) => next.delete(b.id)));
      return next;
    });
  };

  // Global actions operate on the currently visible (searched) set.
  const handleSelectAll = () => {
    const depts = visibleMinistries.flatMap((m) => m.departments ?? []);
    setSelDepts((prev) => {
      const next = new Set(prev);
      depts.forEach((d) => next.add(d.id));
      return next;
    });
    setSelBranches((prev) => {
      const next = new Set(prev);
      depts.filter(isJkrDept).forEach((d) => (d.branches ?? []).forEach((b) => next.delete(b.id)));
      return next;
    });
  };

  // Master "Pilih Semua" checkbox state, scoped to the currently visible set.
  const allVisibleDepts = visibleMinistries.flatMap((m) => m.departments ?? []);
  const allSelected =
    allVisibleDepts.length > 0 && allVisibleDepts.every((d) => selDepts.has(d.id));
  const anyVisibleSelected = allVisibleDepts.some(
    (d) => selDepts.has(d.id) || isJkrPartiallyChecked(d)
  );

  const handleToggleAll = () => {
    if (allSelected) {
      setSelDepts((prev) => {
        const next = new Set(prev);
        allVisibleDepts.forEach((d) => next.delete(d.id));
        return next;
      });
      setSelBranches((prev) => {
        const next = new Set(prev);
        allVisibleDepts
          .filter(isJkrDept)
          .forEach((d) => (d.branches ?? []).forEach((b) => next.delete(b.id)));
        return next;
      });
    } else {
      handleSelectAll();
    }
  };

  const toggleExpandMinistry = (id) => setExpandedMinistries((prev) => toggleInSet(prev, id));
  const toggleExpandDept = (id) => setExpandedDepts((prev) => toggleInSet(prev, id));
  const expandAll = () => {
    setExpandedMinistries(new Set(sortedMinistries.map((m) => m.id)));
    setExpandedDepts(new Set([...deptById.values()].filter(isJkrDept).map((d) => d.id)));
  };
  const collapseAll = () => {
    setExpandedMinistries(new Set());
    setExpandedDepts(new Set());
  };
  const anyExpanded = expandedMinistries.size > 0 || expandedDepts.size > 0;

  const draftCount = selDepts.size + selBranches.size;
  const appliedCount = applied.depts.size + applied.branches.size;
  const setsEqual = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));
  const dirty = !setsEqual(selDepts, applied.depts) || !setsEqual(selBranches, applied.branches);

  // "JKR, JPS +3" for the toggle button.
  const appliedSummary = useMemo(() => {
    const names = [
      ...[...applied.depts].map((id) => deptName(deptById.get(id))),
      ...[...applied.branches].map((id) => branchById.get(id)?.label),
    ].filter(Boolean);
    if (!names.length) return '';
    const shown = names.slice(0, 2).join(', ');
    return names.length > 2 ? `${shown} +${names.length - 2}` : shown;
  }, [applied, deptById, branchById]);

  const handleApply = useCallback(() => {
    setApplied({ depts: new Set(selDepts), branches: new Set(selBranches) });
    onChange({ departments: [...selDepts], jkr_branches: [...selBranches] });
    setShow(false);
  }, [selDepts, selBranches, onChange]);

  const handleReset = () => {
    setSelDepts(new Set());
    setSelBranches(new Set());
    setSearch('');
    setApplied({ depts: new Set(), branches: new Set() });
    onChange({ departments: [], jkr_branches: [] });
    setShow(false);
  };

  // Closing without Tapis discards the draft so the menu always re-opens
  // showing what the table is actually filtered by.
  const handleToggle = (next) => {
    if (!next && dirty) {
      setSelDepts(new Set(applied.depts));
      setSelBranches(new Set(applied.branches));
    }
    if (!next) setSearch('');
    setShow(next);
  };

  // Keyboard: Enter applies, Escape closes (discarding the draft).
  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => {
      if (e.key === 'Enter' && dirty) {
        e.preventDefault();
        handleApply();
      } else if (e.key === 'Escape') {
        handleToggle(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, dirty, handleApply]);

  return (
    <Dropdown show={show} onToggle={handleToggle} autoClose="outside">
      <Dropdown.Toggle as={FilterToggle} count={appliedCount} summary={appliedSummary} />
      <Dropdown.Menu className="p-0 shadow" style={{ width: 440 }}>
        {/* Header: search + global actions */}
        <div className="px-3 pt-3 pb-2 border-bottom">
          <div className="position-relative mb-2">
            <Icon
              path={mdiMagnify}
              size={0.8}
              className="position-absolute top-50 translate-middle-y text-muted"
              style={{ left: 10, pointerEvents: 'none' }}
            />
            <input
              className="form-control form-control-sm"
              placeholder="Cari kementerian / jabatan / cawangan..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{ paddingLeft: 32, paddingRight: 28 }}
            />
            {search && (
              <span
                role="button"
                onClick={() => setSearch('')}
                className="position-absolute top-50 translate-middle-y d-inline-flex text-muted"
                style={{ right: 8, cursor: 'pointer' }}
              >
                <Icon path={mdiClose} size={0.7} />
              </span>
            )}
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <TriCheckbox
              id="min-select-all"
              checked={allSelected}
              indeterminate={anyVisibleSelected && !allSelected}
              onChange={handleToggleAll}
              label={
                <span className="small fw-bold">
                  {term ? 'Pilih semua hasil carian' : 'Pilih Semua'}
                </span>
              }
            />
            <span className="small text-muted ms-auto me-3">{draftCount} dipilih</span>
            {!term && (
              <Button
                size="sm"
                variant="link"
                className="p-0 small text-decoration-none d-inline-flex align-items-center"
                onClick={anyExpanded ? collapseAll : expandAll}
              >
                <Icon
                  path={anyExpanded ? mdiUnfoldLessHorizontal : mdiUnfoldMoreHorizontal}
                  size={0.6}
                  className="me-1"
                />
                {anyExpanded ? 'Kuncupkan semua' : 'Kembangkan semua'}
              </Button>
            )}
          </div>
        </div>

        {/* Body: tree */}
        <div style={{ maxHeight: 340, overflowY: 'auto' }} className="px-2 py-2">
          {visibleMinistries.length === 0 ? (
            <div className="text-muted small px-2 py-3 text-center">
              Tiada padanan untuk “{search.trim()}”
            </div>
          ) : (
            visibleMinistries.map((ministry) => {
              const depts = ministry.departments ?? [];
              const mState = ministryState(ministry);
              const mExpanded = term ? true : expandedMinistries.has(ministry.id);
              return (
                <div key={ministry.id} className="mb-1">
                  <div
                    className="mfilter-row d-flex align-items-center px-1 py-1"
                    onClick={() => depts.length && toggleExpandMinistry(ministry.id)}
                    style={{ cursor: depts.length ? 'pointer' : 'default' }}
                  >
                    <span
                      className="me-1 text-muted d-inline-flex"
                      style={{ visibility: depts.length ? 'visible' : 'hidden' }}
                    >
                      <Icon path={mExpanded ? mdiChevronDown : mdiChevronRight} size={0.7} />
                    </span>
                    <div onClick={(e) => e.stopPropagation()} className="flex-grow-1 min-w-0">
                      <TriCheckbox
                        id={`min-${ministry.id}`}
                        checked={mState.checked}
                        indeterminate={mState.indeterminate}
                        onChange={() => handleToggleMinistry(ministry)}
                        label={<span className="fw-bold">{ministry.name}</span>}
                      />
                    </div>
                    {/* Selected-count hint stays visible while collapsed */}
                    {mState.selected > 0 && (
                      <Badge bg="primary" pill className="ms-2 flex-shrink-0 fw-normal">
                        {mState.selected}/{mState.total}
                      </Badge>
                    )}
                  </div>

                  {mExpanded &&
                    depts.map((dept) => {
                      const isJkr = isJkrDept(dept);
                      const branches = dept.branches ?? [];
                      const dExpanded = term ? true : expandedDepts.has(dept.id);
                      const expandable = isJkr && branches.length > 0;
                      return (
                        <div key={dept.id} style={{ paddingLeft: 22 }}>
                          <div
                            className="mfilter-row d-flex align-items-center px-1 py-1"
                            onClick={() => expandable && toggleExpandDept(dept.id)}
                            style={{ cursor: expandable ? 'pointer' : 'default' }}
                          >
                            <span
                              className="me-1 text-muted d-inline-flex"
                              style={{ visibility: expandable ? 'visible' : 'hidden' }}
                            >
                              <Icon
                                path={dExpanded ? mdiChevronDown : mdiChevronRight}
                                size={0.6}
                              />
                            </span>
                            <div onClick={(e) => e.stopPropagation()} className="flex-grow-1 min-w-0">
                              <TriCheckbox
                                id={`dept-${dept.id}`}
                                checked={isDeptFullyChecked(dept)}
                                indeterminate={isJkrPartiallyChecked(dept)}
                                onChange={() => handleToggleDept(dept)}
                                label={<span>{dept.value}</span>}
                              />
                            </div>
                            {expandable && isJkrPartiallyChecked(dept) && (
                              <span className="small text-muted ms-2 flex-shrink-0">
                                {branches.filter((b) => selBranches.has(b.id)).length}/{branches.length} cawangan
                              </span>
                            )}
                          </div>

                          {isJkr &&
                            dExpanded &&
                            branches.map((branch) => (
                              <div
                                key={branch.id}
                                className="mfilter-row d-flex align-items-center px-1 py-1"
                                style={{ paddingLeft: 44 }}
                              >
                                <TriCheckbox
                                  id={`branch-${branch.id}`}
                                  checked={selDepts.has(dept.id) || selBranches.has(branch.id)}
                                  onChange={() => handleToggleBranch(dept, branch)}
                                  label={<span className="text-muted">{branch.label}</span>}
                                />
                              </div>
                            ))}
                        </div>
                      );
                    })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer: apply / reset */}
        <div className="d-flex align-items-center gap-2 px-3 py-2 border-top">
          <Button
            size="sm"
            variant="primary"
            className="flex-fill"
            onClick={handleApply}
            disabled={!dirty}
          >
            Tapis{draftCount > 0 ? ` (${draftCount})` : ''}
          </Button>
          <Button
            size="sm"
            variant="light"
            className="flex-fill"
            onClick={handleReset}
            disabled={draftCount === 0 && appliedCount === 0}
          >
            Set Semula
          </Button>
        </div>
        {dirty && (
          <div className="px-3 pb-2 small text-muted text-center">
            Perubahan belum ditapis — tekan Tapis atau Enter.
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default MinistryFilter;
